import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBookingWizard } from 'core/contexts/bookingWizard';
import { useCurrency } from 'core/contexts/currency';
import { alpha, Box, Button, Collapse, Divider, FormHelperText, Grid2, IconButton, Paper, Slide, Typography, useMediaQuery } from '@mui/material';
import { DeleteOutlined, EditOutlined, ExpandLess, ExpandMore } from '@mui/icons-material';
import { formatCurrencyWithDecimals } from '@utriper/core';

const Summary = ({ handleSubmit = null, setBottomSpace, label, disabled = false, loading = false, hideHeader = false, hideBack = true }) => {
    const { i18n } = useTranslation();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { checkoutItems, handleBack, handleNext, pricing, hasErrors, handleOpenEdit, handleOpenDelete } = useBookingWizard();
    const { currency } = useCurrency();
    const summaryRef = useRef(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (summaryRef.current) {
            setBottomSpace(summaryRef.current.offsetHeight);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutItems?.length]);

    const paperStyles = isMobile
        ? {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              mx: '1em',
              my: '0.5em',
              padding: '1em',
              // A fixed element is not scrolled by the page, so it must fit the screen on its own:
              // below the mobile stepper (the steps' 6rem top padding) and its own 0.5em margins.
              maxHeight: 'calc(100dvh - 6rem - 1em)',
              overflowY: 'auto',
              borderRadius: '.5em',
              border: hasErrors > 0 ? 'solid 1px #C51313' : 'solid 1px #13131340',
              backgroundColor: hasErrors > 0 ? '#FCF3F3' : 'white',
          }
        : {
              width: '100%',
              maxWidth: '480px',
              height: 'fit-content',
              padding: '1em',
          };

    return (
        <Slide direction="up" in={checkoutItems?.length > 0} mountOnEnter unmountOnExit>
            <Paper ref={summaryRef} elevation={isMobile ? 3 : 0} sx={{ ...paperStyles }}>
                <Grid2 container size={12} spacing={2} direction="column">
                    {/* Header */}
                    {!hideHeader && (
                        <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                            <Grid2 container direction="column" gap={0.5}>
                                <Typography fontSize={16} sx={{ color: hasErrors > 0 ? '#C51313' : '#131313' }}>{`${checkoutItems.length} Experiencia(s)`}</Typography>

                                {hasErrors > 0 && <Typography fontSize={12} sx={{ color: '#C51313' }}>{`${hasErrors} Experiencia(s) tiene errores!`}</Typography>}
                            </Grid2>
                            <IconButton onClick={() => setOpen(!open)}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
                        </Grid2>
                    )}

                    <Collapse in={open && !hideHeader} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                        <Grid2 container direction="column" gap={1}>
                            {checkoutItems?.map((checkoutItem) => (
                                <Grid2 key={checkoutItem?.id} container size={12} gap={1} py={1} flexWrap="nowrap">
                                    {/* Name and Price */}
                                    <Grid2 container size={9} justifyContent="space-between" alignItems="center">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <Typography fontSize={14} noWrap>
                                                {`${checkoutItem?.name} ${checkoutItem?.bundleName ? `(${checkoutItem.bundleName})` : ''}`}
                                            </Typography>
                                            {Object.values(checkoutItem?.errors ?? {})?.length > 0 &&
                                                Object.values(checkoutItem?.errors ?? {}).map((error, index) => (
                                                    <FormHelperText
                                                        key={`${error}-${index}`}
                                                        error
                                                        sx={{
                                                            textOverflow: 'ellipsis',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {error}
                                                    </FormHelperText>
                                                ))}
                                        </Box>

                                        <Typography>{checkoutItem?.pricing?.payTotal > 0 ? formatCurrencyWithDecimals(checkoutItem.pricing?.payTotal, currency, i18n.language, 0) : checkoutItem?.pricing?.payTotal === 0 ? '¡Gratis!' : ''}</Typography>
                                    </Grid2>

                                    <Divider flexItem orientation="vertical" sx={{ borderColor: alpha('#131313', 0.25) }} />

                                    {/* Buttons */}
                                    <Grid2 container size={3} justifyContent="space-between" alignItems="center">
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={() => {
                                                handleOpenEdit(checkoutItem);
                                            }}
                                        >
                                            <EditOutlined />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                handleOpenDelete({ id: checkoutItem?.id, name: checkoutItem?.name });
                                            }}
                                        >
                                            <DeleteOutlined />
                                        </IconButton>
                                    </Grid2>
                                </Grid2>
                            ))}
                        </Grid2>
                    </Collapse>

                    <Divider flexItem sx={{ display: hideHeader ? 'none' : 'block', borderColor: alpha('#131313', 0.25), mt: 1 }} />

                    <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                        <Typography fontSize={16}>Total:</Typography>
                        <Typography fontSize={16} fontWeight={600}>
                            {formatCurrencyWithDecimals(pricing?.payTotal, currency, i18n.language, 0)}
                        </Typography>
                    </Grid2>

                    <Grid2 container size={12} justifyContent="space-between" alignItems="center">
                        <Typography fontSize={16}>A pagar ahora:</Typography>
                        <Typography fontSize={16} fontWeight={600} sx={{ color: '#137DC5' }}>
                            {formatCurrencyWithDecimals(pricing?.payNow, currency, i18n.language, 0)}
                        </Typography>
                    </Grid2>

                    <Grid2 container justifyContent="center" alignItems="center" gap={1.5} flexWrap="nowrap">
                        {!hideBack && (
                            <Button
                                sx={{
                                    border: '1px solid #13131340',
                                    color: '#131313',
                                    width: '100%',
                                    fontWeight: 600,
                                }}
                                variant="outlined"
                                onClick={handleBack}
                            >
                                Volver
                            </Button>
                        )}
                        <Button
                            sx={{
                                fontWeight: 600,
                                width: '100%',
                                color: 'white',
                                backgroundColor: '#137DC5',
                            }}
                            variant="contained"
                            loading={loading}
                            onClick={handleSubmit || handleNext}
                            disabled={disabled || loading || hasErrors > 0}
                        >
                            {label || 'Continuar'}
                        </Button>
                    </Grid2>
                </Grid2>
            </Paper>
        </Slide>
    );
};

export default Summary;
